import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, IconButton, Dialog, DialogTitle, DialogContent, DialogActions, TextField, MenuItem, Alert, TablePagination, TableSortLabel, Checkbox, Toolbar
} from '@mui/material';
import { Add, Edit, Delete, Settings, History } from '@mui/icons-material';
import { getUser } from '../utils/auth';
import Menu from '@mui/material/Menu';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';
import Tooltip from '@mui/material/Tooltip';

function exportToCSV(data, filename) {
  const csvRows = [];
  const headers = ['First Name', 'Last Name', 'Organization', 'Plan', 'Status', 'Start Date', 'End Date'];
  csvRows.push(headers.join(','));
  for (const sub of data) {
    csvRows.push([
      `"${(sub.firstName || '').replace(/"/g, '""')}"`,
      `"${(sub.lastName || '').replace(/"/g, '""')}"`,
      `"${(sub.organization?.Name || '').replace(/"/g, '""')}"`,
      `"${(sub.plan?.name || '').replace(/"/g, '""')}"`,
      `"${(sub.status || '').replace(/"/g, '""')}"`,
      `"${sub.startDate ? sub.startDate.slice(0, 10) : ''}"`,
      `"${sub.endDate ? sub.endDate.slice(0, 10) : ''}"`,
    ].join(','));
  }
  const csvString = csvRows.join('\n');
  const blob = new Blob([csvString], { type: 'text/csv' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  window.URL.revokeObjectURL(url);
}

function mergeColumns(saved, defaults) {
  if (!saved) return defaults;
  const byId = Object.fromEntries(saved.map((c) => [c.id, c]));
  return defaults.map((col) => ({ ...col, ...(byId[col.id] || {}) }));
}

function Subscriptions() {
  const [subs, setSubs] = useState([]);
  const [plans, setPlans] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editSub, setEditSub] = useState(null);
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    mobileNo: '',
    email: '',
    plan: '',
    organization: '',
    status: 'active',
  });
  const currentUser = getUser();
  const isAdmin = currentUser?.role === 'admin';
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [orderBy, setOrderBy] = useState('plan');
  const [order, setOrder] = useState('asc');
  const [selected, setSelected] = useState([]);
  const defaultColumns = [
    { id: 'firstName', label: 'First Name', visible: true },
    { id: 'lastName', label: 'Last Name', visible: true },
    { id: 'organization', label: 'Organization', visible: true },
    { id: 'mobileNo', label: 'Mobile No', visible: true },
    { id: 'email', label: 'Email', visible: true },
    { id: 'plan', label: 'Plan', visible: true },
    { id: 'startDate', label: 'Start Date', visible: true },
    { id: 'endDate', label: 'End Date', visible: true },
    { id: 'actions', label: 'Actions', visible: true },
  ];
  const [columns, setColumns] = useState(() => {
    const saved = localStorage.getItem('subscriptionsTableColumns');
    return mergeColumns(saved ? JSON.parse(saved) : null, defaultColumns);
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [historyDialog, setHistoryDialog] = useState({ open: false, subId: null, history: [], loading: false });

  const fetchSubs = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiGet('/api/subscriptions');
      setSubs(Array.isArray(res.data) ? res.data : []);
      setSelected([]);
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchPlans = async () => {
    try {
      const res = await apiGet('/api/subscription-plans');
      setPlans(Array.isArray(res.data) ? res.data : []);
    } catch {}
  };

  const fetchOrganizations = async () => {
    try {
      const res = await apiGet('/api/organizations');
      setOrganizations(Array.isArray(res.data) ? res.data : []);
    } catch {}
  };

  useEffect(() => {
    fetchSubs();
    fetchPlans();
    fetchOrganizations();
  }, []);

  const handleOpenDialog = (sub = null) => {
    setEditSub(sub);
    setForm(sub ? {
      firstName: sub.firstName || '',
      lastName: sub.lastName || '',
      mobileNo: sub.mobileNo || '',
      email: sub.email || '',
      plan: sub.plan?._id || sub.plan || '',
      organization: sub.organization?._id || sub.organization || '',
      status: sub.status || 'active',
    } : {
      firstName: '',
      lastName: '',
      mobileNo: '',
      email: '',
      plan: '',
      organization: '',
      status: 'active',
    });
    setOpenDialog(true);
    setError('');
    setSuccess('');
  };
  const handleCloseDialog = () => setOpenDialog(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      let res;
      if (editSub) {
        res = await apiPut(`/api/subscriptions/${editSub._id}`, form);
        if (!res.status || res.status >= 400) throw new Error(res.data?.message || 'Update failed');
        setSuccess('Subscription updated');
      } else {
        res = await apiPost('/api/subscriptions', form);
        if (!res.status || res.status >= 400) throw new Error(res.data?.message || 'Create failed');
        setSuccess('Subscription created');
      }
      handleCloseDialog();
      await fetchSubs();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this subscription?')) return;
    setError('');
    setSuccess('');
    try {
      const res = await apiDelete(`/api/subscriptions/${id}`);
      if (!res.status || res.status >= 400) throw new Error(res.data?.message || 'Delete failed');
      setSuccess('Subscription deleted');
      await fetchSubs();
    } catch (err) {
      setError(err.response?.data?.message || err.message);
    }
  };

  function sortComparator(a, b) {
    let valA = a[orderBy];
    let valB = b[orderBy];
    if (orderBy === 'plan') {
      valA = a.plan?.name || '';
      valB = b.plan?.name || '';
    } else if (orderBy === 'organization') {
      valA = a.organization?.Name || '';
      valB = b.organization?.Name || '';
    } else if (orderBy === 'startDate' || orderBy === 'endDate') {
      valA = valA || '';
      valB = valB || '';
    } else {
      valA = valA?.toLowerCase?.() || '';
      valB = valB?.toLowerCase?.() || '';
    }
    if (typeof valA === 'string') valA = valA.toLowerCase();
    if (typeof valB === 'string') valB = valB.toLowerCase();
    if (valA < valB) return order === 'asc' ? -1 : 1;
    if (valA > valB) return order === 'asc' ? 1 : -1;
    return 0;
  }

  const filteredSubs = subs.filter((sub) => {
    const q = search.toLowerCase();
    return (
      (sub.firstName || '').toLowerCase().includes(q) ||
      (sub.lastName || '').toLowerCase().includes(q) ||
      (sub.email || '').toLowerCase().includes(q) ||
      (sub.plan?.name || '').toLowerCase().includes(q) ||
      (sub.organization?.Name || '').toLowerCase().includes(q) ||
      (sub.organization?.Code || '').toLowerCase().includes(q)
    );
  }).sort(sortComparator);

  const paginatedSubs = filteredSubs.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleBulkDelete = async () => {
    if (!selected.length) return;
    if (!window.confirm(`Delete ${selected.length} selected subscription(s)?`)) return;
    setError('');
    setSuccess('');
    try {
      const res = await apiPost('/api/subscriptions/bulk-delete', { ids: selected });
      if (!res.status || res.status >= 400) throw new Error(res.data?.message || 'Bulk delete failed');
      setSuccess(res.data?.message || 'Selected subscriptions deleted');
      setSelected([]);
      await fetchSubs();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Bulk delete failed');
    }
  };

  const isSelected = (id) => selected.indexOf(id) !== -1;
  const handleSelectAllClick = (e) => {
    if (e.target.checked) {
      setSelected(paginatedSubs.map((s) => s._id));
      return;
    }
    setSelected([]);
  };
  const handleClick = (e, id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];
    if (selectedIndex === -1) newSelected = newSelected.concat(selected, id);
    else if (selectedIndex === 0) newSelected = newSelected.concat(selected.slice(1));
    else if (selectedIndex === selected.length - 1) newSelected = newSelected.concat(selected.slice(0, -1));
    else if (selectedIndex > 0) {
      newSelected = newSelected.concat(selected.slice(0, selectedIndex), selected.slice(selectedIndex + 1));
    }
    setSelected(newSelected);
  };

  const handleColumnsClick = (e) => setAnchorEl(e.currentTarget);
  const handleColumnsClose = () => setAnchorEl(null);
  const handleColumnToggle = (id) => {
    const newCols = columns.map((col) => (col.id === id ? { ...col, visible: !col.visible } : col));
    setColumns(newCols);
    localStorage.setItem('subscriptionsTableColumns', JSON.stringify(newCols));
  };

  const handleShowHistory = async (subId) => {
    setHistoryDialog({ open: true, subId, history: [], loading: true });
    try {
      const res = await apiGet(`/api/subscriptions/${subId}/history`);
      setHistoryDialog({ open: true, subId, history: res.data || [], loading: false });
    } catch {
      setHistoryDialog({ open: true, subId, history: [], loading: false });
    }
  };
  const handleCloseHistory = () => setHistoryDialog({ open: false, subId: null, history: [], loading: false });

  const isColVisible = (id) => !!columns.find((c) => c.id === id && c.visible);

  return (
    <Box>
      <Typography variant="h4" mb={3}>Subscriptions</Typography>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Search by name, email or organization"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
        />
        <Button variant="outlined" onClick={() => exportToCSV(filteredSubs, 'subscriptions.csv')}>
          Export CSV
        </Button>
        <Tooltip title="Customize columns">
          <IconButton onClick={handleColumnsClick}><Settings /></IconButton>
        </Tooltip>
        <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleColumnsClose}>
          {columns.map((col) => (
            <MenuItem key={col.id} onClick={() => handleColumnToggle(col.id)}>
              <Checkbox checked={col.visible} />
              {col.label}
            </MenuItem>
          ))}
        </Menu>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
      {isAdmin && (
        <Button variant="contained" startIcon={<Add />} sx={{ mb: 2 }} onClick={() => handleOpenDialog()}>
          Add Subscription
        </Button>
      )}
      {isAdmin && selected.length > 0 && (
        <Toolbar sx={{ bgcolor: 'rgba(0,0,0,0.04)', mb: 1 }}>
          <Typography sx={{ flex: 1 }}>{selected.length} selected</Typography>
          <Button color="error" variant="contained" onClick={handleBulkDelete}>Delete Selected</Button>
        </Toolbar>
      )}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              {isAdmin && (
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selected.length > 0 && selected.length < paginatedSubs.length}
                    checked={paginatedSubs.length > 0 && selected.length === paginatedSubs.length}
                    onChange={handleSelectAllClick}
                    inputProps={{ 'aria-label': 'select all subscriptions' }}
                  />
                </TableCell>
              )}
              {isColVisible('firstName') && (
                <TableCell sortDirection={orderBy === 'firstName' ? order : false}>
                  <TableSortLabel active={orderBy === 'firstName'} direction={orderBy === 'firstName' ? order : 'asc'} onClick={() => handleSort('firstName')}>
                    First Name
                  </TableSortLabel>
                </TableCell>
              )}
              {isColVisible('lastName') && (
                <TableCell sortDirection={orderBy === 'lastName' ? order : false}>
                  <TableSortLabel active={orderBy === 'lastName'} direction={orderBy === 'lastName' ? order : 'asc'} onClick={() => handleSort('lastName')}>
                    Last Name
                  </TableSortLabel>
                </TableCell>
              )}
              {isColVisible('organization') && (
                <TableCell sortDirection={orderBy === 'organization' ? order : false}>
                  <TableSortLabel active={orderBy === 'organization'} direction={orderBy === 'organization' ? order : 'asc'} onClick={() => handleSort('organization')}>
                    Organization
                  </TableSortLabel>
                </TableCell>
              )}
              {isColVisible('mobileNo') && (
                <TableCell sortDirection={orderBy === 'mobileNo' ? order : false}>
                  <TableSortLabel active={orderBy === 'mobileNo'} direction={orderBy === 'mobileNo' ? order : 'asc'} onClick={() => handleSort('mobileNo')}>
                    Mobile No
                  </TableSortLabel>
                </TableCell>
              )}
              {isColVisible('email') && (
                <TableCell sortDirection={orderBy === 'email' ? order : false}>
                  <TableSortLabel active={orderBy === 'email'} direction={orderBy === 'email' ? order : 'asc'} onClick={() => handleSort('email')}>
                    Email
                  </TableSortLabel>
                </TableCell>
              )}
              {isColVisible('plan') && (
                <TableCell sortDirection={orderBy === 'plan' ? order : false}>
                  <TableSortLabel active={orderBy === 'plan'} direction={orderBy === 'plan' ? order : 'asc'} onClick={() => handleSort('plan')}>
                    Plan
                  </TableSortLabel>
                </TableCell>
              )}
              {isColVisible('startDate') && (
                <TableCell sortDirection={orderBy === 'startDate' ? order : false}>
                  <TableSortLabel active={orderBy === 'startDate'} direction={orderBy === 'startDate' ? order : 'asc'} onClick={() => handleSort('startDate')}>
                    Start Date
                  </TableSortLabel>
                </TableCell>
              )}
              {isColVisible('endDate') && (
                <TableCell sortDirection={orderBy === 'endDate' ? order : false}>
                  <TableSortLabel active={orderBy === 'endDate'} direction={orderBy === 'endDate' ? order : 'asc'} onClick={() => handleSort('endDate')}>
                    End Date
                  </TableSortLabel>
                </TableCell>
              )}
              {isAdmin && isColVisible('actions') && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && paginatedSubs.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align="center">No subscriptions found</TableCell>
              </TableRow>
            )}
            {paginatedSubs.map((sub) => {
              const isItemSelected = isSelected(sub._id);
              return (
                <TableRow
                  key={sub._id}
                  hover
                  role="checkbox"
                  aria-checked={isItemSelected}
                  tabIndex={-1}
                  selected={isItemSelected}
                >
                  {isAdmin && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isItemSelected}
                        onChange={(e) => handleClick(e, sub._id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </TableCell>
                  )}
                  {isColVisible('firstName') && <TableCell>{sub.firstName}</TableCell>}
                  {isColVisible('lastName') && <TableCell>{sub.lastName}</TableCell>}
                  {isColVisible('organization') && (
                    <TableCell>
                      {sub.organization?.Name || '-'}
                      {sub.organization?.Code ? ` (${sub.organization.Code})` : ''}
                    </TableCell>
                  )}
                  {isColVisible('mobileNo') && <TableCell>{sub.mobileNo}</TableCell>}
                  {isColVisible('email') && <TableCell>{sub.email}</TableCell>}
                  {isColVisible('plan') && <TableCell>{sub.plan?.name || ''}</TableCell>}
                  {isColVisible('startDate') && <TableCell>{sub.startDate ? sub.startDate.slice(0, 10) : ''}</TableCell>}
                  {isColVisible('endDate') && <TableCell>{sub.endDate ? sub.endDate.slice(0, 10) : ''}</TableCell>}
                  {isAdmin && isColVisible('actions') && (
                    <TableCell align="right">
                      <Tooltip title="Edit">
                        <IconButton onClick={(e) => { e.stopPropagation(); handleOpenDialog(sub); }}><Edit /></IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton onClick={(e) => { e.stopPropagation(); handleDelete(sub._id); }}><Delete /></IconButton>
                      </Tooltip>
                      <Tooltip title="View History">
                        <IconButton onClick={(e) => { e.stopPropagation(); handleShowHistory(sub._id); }}><History /></IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredSubs.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editSub ? 'Edit Subscription' : 'Add Subscription'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <TextField label="First Name" name="firstName" value={form.firstName} onChange={handleChange} fullWidth margin="normal" required />
            <TextField label="Last Name" name="lastName" value={form.lastName} onChange={handleChange} fullWidth margin="normal" required />
            <TextField label="Mobile No" name="mobileNo" value={form.mobileNo} onChange={handleChange} fullWidth margin="normal" required />
            <TextField label="Email" name="email" value={form.email} onChange={handleChange} fullWidth margin="normal" required />
            <TextField
              label="Organization"
              name="organization"
              select
              value={form.organization}
              onChange={handleChange}
              fullWidth
              margin="normal"
              required
            >
              {organizations.map((org) => (
                <MenuItem key={org._id} value={org._id}>
                  {org.Name} ({org.Code})
                </MenuItem>
              ))}
            </TextField>
            <TextField label="Plan" name="plan" select value={form.plan} onChange={handleChange} fullWidth margin="normal" required>
              {plans.map((plan) => <MenuItem key={plan._id} value={plan._id}>{plan.name}</MenuItem>)}
            </TextField>
            <TextField label="Status" name="status" select value={form.status} onChange={handleChange} fullWidth margin="normal" required>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button type="submit" variant="contained">{editSub ? 'Update' : 'Create'}</Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog open={historyDialog.open} onClose={handleCloseHistory} maxWidth="md" fullWidth>
        <DialogTitle>Subscription History</DialogTitle>
        <DialogContent>
          {historyDialog.loading ? <Typography>Loading...</Typography> :
            historyDialog.history.length === 0 ? <Typography>No history found.</Typography> :
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Action</TableCell>
                    <TableCell>Changed By</TableCell>
                    <TableCell>Changed At</TableCell>
                    <TableCell>Data Snapshot</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {historyDialog.history.map((h, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{h.action}</TableCell>
                      <TableCell>
                        {h.changedBy?.firstName
                          ? `${h.changedBy.firstName} ${h.changedBy.lastName || ''}`.trim()
                          : (h.changedBy?.email || h.changedBy)}
                      </TableCell>
                      <TableCell>{new Date(h.changedAt).toLocaleString()}</TableCell>
                      <TableCell>
                        <pre style={{ maxWidth: 400, whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                          {(() => {
                            const data = { ...h.data };
                            if (data.plan) {
                              const planObj = plans.find((p) => p._id === (data.plan._id || data.plan));
                              data.plan = planObj ? planObj.name : data.plan;
                            }
                            if (data.organization) {
                              const orgObj = organizations.find((o) => o._id === (data.organization._id || data.organization));
                              data.organization = orgObj ? orgObj.Name : data.organization;
                            }
                            return JSON.stringify(data, null, 2);
                          })()}
                        </pre>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
          }
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseHistory}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Subscriptions;
